/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { useEffect, useRef } from "react";

import { useDeviceType } from "../../shared/hooks/useDeviceType";
import {
  usePlayerAdvertisementCue,
  usePlayerController,
  usePlayerFeatures,
  usePlayerSettings,
  usePlayerTimer,
} from "../BasePlayer";

import { usePlayerCurrentMedia } from "./PlayerCurrentMediaContext";

export const usePlayerNextEpisode = (onShow: (timer: number) => void) => {
  const { iPhoneDevice } = useDeviceType();
  const { player } = usePlayerController();
  const { nextEpisode: nextEpisodeEnabled } = usePlayerFeatures();
  const { nextMedia } = usePlayerCurrentMedia();
  const { havePostroll } = usePlayerAdvertisementCue();

  const {
    timer: { duration },
  } = usePlayerTimer();

  const {
    nextEpisode: { thresholdSeconds },
  } = usePlayerSettings();

  const firedRef = useRef<boolean>(false);
  const wasPlayingRef = useRef<boolean>(false);
  const fromSeekRef = useRef<boolean>(false);

  const isValidDuration = duration && duration !== Infinity;
  const nextEpisodeAvailable =
    nextEpisodeEnabled && nextMedia && isValidDuration;

  useEffect(() => {
    return () => {
      firedRef.current = false;
    };
  }, []);

  // Detect if user seeked to next episode time window
  useEffect(() => {
    const unsubscribe = player.onEvent("seeked", () => {
      fromSeekRef.current =
        player.currentTime() >= player.duration() - thresholdSeconds;
    });

    return () => unsubscribe();
  }, [player, thresholdSeconds]);

  // Detect if player was playing before time updated
  useEffect(() => {
    const unsubscribePlaying = player.onEvent("playing", () => {
      wasPlayingRef.current = true;
    });

    const unsubscribePause = player.onEvent("pause", () => {
      wasPlayingRef.current = false;
    });

    return () => {
      unsubscribePlaying();
      unsubscribePause();
    };
  }, [player]);

  // Show next episode when video ended
  useEffect(() => {
    const unsubscribe = player.onEvent("ended", () => {
      // To show custom UI on iPhone, we need to exit native full screen
      iPhoneDevice && player.exitNativeFullScreen();

      nextEpisodeAvailable && !firedRef.current && onShow(thresholdSeconds);
    });

    return () => unsubscribe();
  }, [iPhoneDevice, nextEpisodeAvailable, onShow, player, thresholdSeconds]);

  // Show next episode in valid time window
  useEffect(() => {
    // Use timeupdate event here instead of time context because of update
    // lag in context
    const unsubscribe = player.onEvent("timeupdate", () => {
      const playerCurrentTime = player.currentTime();
      const playerDuration = player.duration();

      // We don't want to show next episode overlay when user jumped to valid
      // window from continue watching position or when manually seeked to that
      // window, in that case next episode will be show only when use seek again
      // out of valid window or video will end
      const show = Boolean(
        nextEpisodeAvailable &&
          wasPlayingRef.current &&
          !fromSeekRef.current &&
          // disable for iPhone as well since we cant show custom UI while
          // video is playing
          !iPhoneDevice &&
          playerCurrentTime > 0 &&
          playerDuration > 0 &&
          playerCurrentTime >= playerDuration - thresholdSeconds &&
          !havePostroll,
      );

      if (show && !firedRef.current) {
        firedRef.current = true;
        onShow(thresholdSeconds);
      }

      // If the button was already displayed, but the user
      // seeked back to the time before the valid window,
      // we want to allow for the button to appear again
      if (
        playerCurrentTime < playerDuration - thresholdSeconds &&
        firedRef.current
      ) {
        firedRef.current = false;
      }
    });

    return () => unsubscribe();
  }, [
    iPhoneDevice,
    havePostroll,
    nextEpisodeAvailable,
    onShow,
    player,
    thresholdSeconds,
  ]);
};
